import * as THREE from 'three';
import { FIREWOOD as F } from '../config/firewood';
import { mulberry32 } from '../utils/prng';
import { boxCenter, boxSize, type Box } from '../world/Level';

type Point = [number, number];

/** A split log along Z: a flat split face, a faceted bark arc and two sawn ends. */
export function splitLogGeometry(variant: number): THREE.BufferGeometry {
  const profile: Point[] = [[0, 0], [1, 0], [0.98, 0.4], [0.82, 0.8], [0.5, 1], [0.14, 0.82], [0.02, 0.4]];
  const angle = (variant % 2) * Math.PI + (variant > 1 ? 0.09 : -0.04);
  const rotated = profile.map(([x, y]): Point => [
    (x - 0.5) * Math.cos(angle) - (y - 0.5) * Math.sin(angle),
    (x - 0.5) * Math.sin(angle) + (y - 0.5) * Math.cos(angle),
  ]);
  const minX = Math.min(...rotated.map(p => p[0]));
  const maxX = Math.max(...rotated.map(p => p[0]));
  const minY = Math.min(...rotated.map(p => p[1]));
  const maxY = Math.max(...rotated.map(p => p[1]));
  const points = rotated.map(([x, y]): Point => [(x - minX) / (maxX - minX) - 0.5, (y - minY) / (maxY - minY) - 0.5]);
  const positions: number[] = [];
  const uvs: number[] = [];
  const geometry = new THREE.BufferGeometry();
  const vertex = (point: Point, z: number, uv: Point) => {
    positions.push(point[0], point[1], z);
    uvs.push(...uv);
  };
  for (let i = 0; i < points.length; i++) {
    const next = (i + 1) % points.length;
    const start = positions.length / 3;
    vertex(points[i], -0.5, [0, 0]);
    vertex(points[next], -0.5, [1, 0]);
    vertex(points[next], 0.5, [1, 1]);
    vertex(points[i], -0.5, [0, 0]);
    vertex(points[next], 0.5, [1, 1]);
    vertex(points[i], 0.5, [0, 1]);
    geometry.addGroup(start, 6, i === 0 ? 1 : 0);
  }
  // End UVs stay attached to the unrotated log, with the pith on the split face.
  for (const z of [-0.5, 0.5]) {
    const start = positions.length / 3;
    for (let i = 1; i < points.length - 1; i++) {
      const order = z > 0 ? [0, i, i + 1] : [0, i + 1, i];
      for (const at of order) vertex(points[at], z, profile[at]);
    }
    geometry.addGroup(start, positions.length / 3 - start, 2);
  }
  // Keep each material contiguous: three draws per variant, not one per face.
  const groups = [...geometry.groups];
  geometry.clearGroups();
  const sortedPositions: number[] = [];
  const sortedUvs: number[] = [];
  for (let material = 0; material < 3; material++) {
    const start = sortedPositions.length / 3;
    for (const group of groups.filter(group => group.materialIndex === material)) {
      sortedPositions.push(...positions.slice(group.start * 3, (group.start + group.count) * 3));
      sortedUvs.push(...uvs.slice(group.start * 2, (group.start + group.count) * 2));
    }
    geometry.addGroup(start, sortedPositions.length / 3 - start, material);
  }
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(sortedPositions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(sortedUvs, 2));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  return geometry;
}

/** Fill the original collision box with staggered courses, never widening the route. */
export function firewoodSlots(box: Box): Box[] {
  const size = boxSize(box);
  const rows = Math.max(1, Math.round(size.y / F.billetHeight));
  const columns = Math.max(1, Math.round(size.x / F.billetWidth));
  const layers = Math.max(1, Math.round(size.z / F.billetLength));
  const pitchX = size.x / columns;
  const pitchY = size.y / rows;
  const pitchZ = size.z / layers;
  const rng = mulberry32(F.seed);
  const slots: Box[] = [];
  for (let layer = 0; layer < layers; layer++) {
    for (let row = 0; row < rows; row++) {
      const offset = row % 2 * 0.5;
      const edges = [0];
      for (let col = offset ? -1 : 0; col < columns; col++) {
        const right = Math.min(columns, col + offset + 1);
        edges.push(right === columns ? size.x : (right + (rng() - 0.5) * 0.25) * pitchX);
      }
      for (let col = 0; col < edges.length - 1; col++) {
        const left = edges[col];
        const right = edges[col + 1];
        slots.push({
          minX: box.minX + left + F.gap,
          maxX: box.minX + right - F.gap,
          minY: box.minY + row * pitchY + F.gap,
          maxY: box.minY + (row + 1) * pitchY - F.gap - rng() * 0.014,
          minZ: box.minZ + layer * pitchZ + F.gap + rng() * 0.035,
          maxZ: box.minZ + (layer + 1) * pitchZ - F.gap - rng() * 0.025,
        });
      }
    }
  }
  return slots;
}

function woodTexture(kind: 'bark' | 'split' | 'end', anisotropy: number): THREE.Texture {
  const canvas = document.createElement('canvas');
  const size = F.textureSize;
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const rng = mulberry32(F.seed + ['bark', 'split', 'end'].indexOf(kind));
  ctx.fillStyle = `#${F[kind].toString(16)}`;
  ctx.fillRect(0, 0, size, size);
  if (kind === 'end') {
    // Rings and radial drying cracks, rather than boards on a sawn end.
    for (let radius = 12; radius < size * 1.5; radius += 9 + rng() * 9) {
      ctx.strokeStyle = `rgba(107,66,28,${0.18 + rng() * 0.18})`;
      ctx.lineWidth = 0.8 + rng();
      ctx.beginPath();
      for (let step = 0; step <= 80; step++) {
        const angle = step / 80 * Math.PI;
        const r = radius + Math.sin(angle * 9 + radius) * 2;
        const x = size / 2 + Math.cos(angle) * r;
        const y = size - Math.sin(angle) * r;
        if (step === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    for (let i = 0; i < 5; i++) {
      const angle = 0.15 + rng() * (Math.PI - 0.3);
      const radius = 100 + rng() * 160;
      ctx.strokeStyle = 'rgba(79,47,24,0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(size / 2 + Math.cos(angle) * radius, size - Math.sin(angle) * radius);
      ctx.lineTo(size / 2 + Math.cos(angle + 0.025) * radius * 0.8, size - Math.sin(angle + 0.025) * radius * 0.8);
      ctx.lineTo(size / 2 + Math.cos(angle) * radius * 0.65, size - Math.sin(angle) * radius * 0.65);
      ctx.stroke();
    }
  } else {
    for (let i = 0; i < (kind === 'bark' ? 190 : 140); i++) {
      const x = rng() * size;
      const y = rng() * size;
      const length = 15 + rng() * 130;
      ctx.strokeStyle = kind === 'bark'
        ? (i % 3 ? 'rgba(24,19,14,0.4)' : 'rgba(172,147,113,0.4)')
        : (i % 3 ? 'rgba(119,75,35,0.2)' : 'rgba(255,232,182,0.35)');
      ctx.lineWidth = kind === 'bark' ? 1 + rng() * 4 : 0.5 + rng();
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rng() - 0.5) * 5, y + length * 0.5);
      ctx.lineTo(x + (rng() - 0.5) * 7, y + length);
      ctx.stroke();
    }
  }
  const pixels = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < pixels.data.length; i += 4) {
    const grain = (rng() - 0.5) * (kind === 'bark' ? 22 : 10);
    for (let channel = 0; channel < 3; channel++) pixels.data[i + channel] += grain;
  }
  ctx.putImageData(pixels, 0, 0);
  const map = new THREE.CanvasTexture(canvas);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = anisotropy;
  return map;
}

/** Geometry and maps shared by every stack; instancing keeps thousands of logs cheap. */
export class Firewood {
  private readonly geometries = Array.from({ length: F.variants }, (_, i) => splitLogGeometry(i));
  private readonly materials: THREE.MeshLambertMaterial[];

  constructor(anisotropy = 1) {
    this.materials = (['bark', 'split', 'end'] as const).map(kind =>
      new THREE.MeshLambertMaterial({ map: woodTexture(kind, anisotropy) }));
  }

  build(boxes: Box[], name: string): THREE.Group {
    const group = new THREE.Group();
    group.name = name;
    const rng = mulberry32(F.seed);
    const variants = boxes.map(() => Math.floor(rng() * F.variants));
    const dummy = new THREE.Object3D();
    for (let variant = 0; variant < F.variants; variant++) {
      const slots = boxes.filter((_, i) => variants[i] === variant);
      if (slots.length === 0) continue;
      const mesh = new THREE.InstancedMesh(this.geometries[variant], this.materials, slots.length);
      for (let i = 0; i < slots.length; i++) {
        const center = boxCenter(slots[i]);
        const size = boxSize(slots[i]);
        dummy.position.set(center.x, center.y, center.z);
        dummy.scale.set(size.x, size.y, size.z);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        mesh.setColorAt(i, new THREE.Color().setScalar(0.84 + rng() * 0.22));
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      group.add(mesh);
    }
    return group;
  }
}
